import React, { useState, useRef, useEffect } from "react";
import { CameraIcon, CancelIcon, AcceptIcon } from "./Icons/Collections";
import "../Styles/Pages/webrtccomp.scss";
import ContentLoader from "react-content-loader";
import Portal from "../Component/Portal";
import { _supportsWebRtc } from "../Utils/helpers";

const WebRtcCamera = props => {
	const width = window.screen.width > 767 ? 400 : window.screen.width - 80;
	const [showModal, setShowModal] = useState(false);
	const [snapStatus, setSnapStatus] = useState("pending");
	const [imageData, setImageData] = useState("");
	const [showLoader, setShowLoader] = useState(true);
	const [height, setHeight] = useState(0);
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const imageRef = useRef(null);
	const inputRef = useRef(null);
	const loaderRef = useRef(true);
	const modalRef = useRef(false);
	const tracksRef = useRef([]);
	const resetTimer = useRef(null);
	const [cameraType, setCameraType] = useState(
		_supportsWebRtc ? "webRtc" : "default",
	);

	useEffect(() => {
		if (navigator.permissions && navigator.permissions.query) {
			try {
				navigator.permissions.query({ name: "camera" }).then(result => {
					if (result && result.state === "denied") {
						setCameraType("default");
					}
				});
			} catch (error) {
				setCameraType("default");
			}
		}
	}, []);

	useEffect(() => {
		loaderRef.current = showLoader;
	}, [showLoader]);

	useEffect(() => {
		modalRef.current = showModal;
		if (showModal) {
			streamVideo();
		}
	}, [showModal]);

	useEffect(() => {
		if (imageData) {
			setSnapStatus("snapped");
		}
	}, [imageData]);

	useEffect(() => {
		if (height) {
			const video = videoRef.current;
			const canvas = canvasRef.current;
			video.setAttribute("width", width);
			video.setAttribute("height", height);
			canvas.setAttribute("width", width);
			canvas.setAttribute("height", height);
			setShowLoader(false);
		}
	}, [height]);

	useEffect(() => {
		if (snapStatus === "snapped") {
			const image = imageRef.current;
			if (image) {
				image.src = imageData;
			}
		}
	}, [snapStatus]);

	function initCapture() {
		if (cameraType === "webRtc") {
			setShowModal(true);
		} else {
			openDefaultCamera();
		}
	}

	function openDefaultCamera() {
		if (inputRef.current) {
			inputRef.current.click();
		}
	}

	async function streamVideo() {
		//if video is yet to start playing after 8 seconds, switch to default camera
		clearTimeout(resetTimer.current);
		resetTimer.current = setTimeout(() => {
			if (loaderRef.current && modalRef.current) {
				resetCamera();
			}
		}, 10000);

		try {
			setShowModal(true);
			const video = videoRef.current;
			navigator.mediaDevices
				.getUserMedia({
					video: { facingMode: props.facingMode || "environment" },
					audio: false,
				})
				.then(stream => {
					video.srcObject = stream;
					tracksRef.current = stream.getTracks();
					video.play();
				})
				.catch(err => {
					resetCamera();
				});
		} catch (error) {
			resetCamera();
		}
	}

	function setVideoAttributes() {
		const video = videoRef.current;
		setHeight(video.videoHeight / (video.videoWidth / width));
	}

	function snapPicture() {
		let canvas = canvasRef.current;
		const video = videoRef.current;
		const context = canvas.getContext("2d");
		if (width && height) {
			canvas.width = width;
			canvas.height = height;
			context.drawImage(video, 0, 0, width, height);
			setImageData(canvas.toDataURL("image/png"));
		}
	}

	function cancelSnap() {
		setSnapStatus("pending");
		setImageData("");
	}

	function closeModal() {
		try {
			if (tracksRef.current && tracksRef.current.length) {
				tracksRef.current.forEach(track => {
					track.stop();
				});
			}
			setHeight(0);
			setImageData("");
			setSnapStatus("pending");
			videoRef.current = null;
			canvasRef.current = null;
			setShowLoader(true);
		} catch (error) {}

		setShowModal(false);
	}

	function savePicture() {
		fetch(imageData)
			.then(res => res.blob())
			.then(blob => {
				const file = new File([blob], `photo-${Math.random()}.png`, {
					type: "image/png",
				});
				closeModal();
				props.savePicture(file);
			});
	}

	function resetCamera() {
		setCameraType("default");
		openDefaultCamera();
		closeModal();
	}

	function snapImage(e) {
		if (e && e.target && e.target.files) {
			props.savePicture(e.target.files[0]);
		}
	}

	return (
		<div className="webrtc-comp">
			<div
				className="webrtc-comp-picture"
				onClick={initCapture}
				style={{
					height: props.labelHeight || "",
					width: props.labelWidth || "",
					border: props.errorText ? "1px solid red" : "",
				}}
			>
				<div className="camera-icon">
					<img src="Images/camera.svg" alt="" />
					{props.labelText || "Upload Photo"}
				</div>
			</div>
			{showModal && (
				<Portal
					header={props.header || "Capture Photo"}
					width={"400px"}
					isSmall
					closeModal={closeModal}
					maxHeight={"440px"}
					forceMobileMaxHeight
					isLight={!!props.isLightBg}
				>
					<div className="webrtc-comp-modal">
						<div className="relative">
							{showLoader && (
								<div className="webrtc-comp-loader">
									<ContentLoader
										height={300}
										width={370}
										speed={2}
										primaryColor="#f3f3f3"
										secondaryColor="#ecebeb"
									>
										<rect x="0" y="0" rx="3" ry="3" width="370" height="300" />
									</ContentLoader>
								</div>
							)}
							<video
								ref={videoRef}
								onCanPlay={setVideoAttributes}
								className="webrtc-comp-video"
							/>

							{imageData && (
								<img ref={imageRef} className="webrtc-comp-image" alt="" />
							)}

							<div className="webrtc-comp-snapactions">
								{snapStatus === "pending" ? (
									<span onClick={snapPicture}>
										<CameraIcon color={"#fff"} height={"26px"} width={"26px"} />
									</span>
								) : (
									<div className="flex align-center justify-center">
										<span
											onClick={cancelSnap}
											style={{ marginRight: "30px", cursor: "pointer" }}
										>
											<CancelIcon
												color={"#fff"}
												height={"20px"}
												width={"20px"}
											/>
										</span>
										<span onClick={savePicture} style={{ cursor: "pointer" }}>
											<AcceptIcon
												color={"#fff"}
												height={"20px"}
												width={"20px"}
											/>
										</span>
									</div>
								)}
							</div>
						</div>
						<canvas ref={canvasRef} className="hide" />
					</div>
				</Portal>
			)}
			<div className="hidden">
				<input
					type="file"
					accept="image/*"
					ref={inputRef}
					onChange={snapImage}
					capture={props.facingMode || "environment"}
				/>
			</div>
		</div>
	);
};

export default WebRtcCamera;
